import {
  CONTACT_FILE_ALLOWED_EXTENSIONS,
  CONTACT_FILE_ALLOWED_MIME_TYPES,
  CONTACT_FILE_MAX_BYTES,
  CONTACT_HISTORY_MAX_ATTACHMENTS,
  ContactHistoryCommunicationKind,
  ContactHistoryKind,
  ContactHistoryKindValue,
} from '@/api/address-book/types';
import type { BadgeColor } from '@/shared/design-system';
import { formatFileSize } from '@/utils/format-file-size';

export const COMMUNICATION_KIND_LABELS: Record<ContactHistoryCommunicationKind, string> = {
  Call: 'Call',
  Message: 'Message',
  Meeting: 'Meeting',
  Note: 'Note',
};

export const COMMUNICATION_KIND_BADGE_COLOR: Record<ContactHistoryCommunicationKind, BadgeColor> = {
  Call: 'primary',
  Message: 'secondary',
  Meeting: 'purple',
  Note: 'default',
};

export const COMPOSER_PLACEHOLDER: Record<ContactHistoryCommunicationKind, string> = {
  Call: 'What was discussed on the call?',
  Message: 'What was said in the message?',
  Meeting: 'What happened at the meeting?',
  Note: 'Add a note about this contact',
};

export const isCommunicationKind = (
  kind: ContactHistoryKindValue,
): kind is ContactHistoryCommunicationKind =>
  kind === ContactHistoryKind.Call ||
  kind === ContactHistoryKind.Message ||
  kind === ContactHistoryKind.Meeting ||
  kind === ContactHistoryKind.Note;

export const UNKNOWN_ACTOR_NAME = 'Unknown';

export const actorName = (actor: { name: string | null } | null | undefined): string =>
  actor?.name?.trim() || UNKNOWN_ACTOR_NAME;

export const CONTACT_FILE_ACCEPT = [
  ...CONTACT_FILE_ALLOWED_MIME_TYPES,
  ...CONTACT_FILE_ALLOWED_EXTENSIONS,
].join(',');

export const CONTACT_FILE_RULES_HINT = `PDF, Word, Excel, PowerPoint, CSV, TXT or images, up to ${formatFileSize(
  CONTACT_FILE_MAX_BYTES,
)} each, ${CONTACT_HISTORY_MAX_ATTACHMENTS} files per entry`;

const ALLOWED_MIME = new Set<string>(CONTACT_FILE_ALLOWED_MIME_TYPES);
const ALLOWED_EXTENSIONS = new Set<string>(CONTACT_FILE_ALLOWED_EXTENSIONS);

const extensionOf = (name: string): string => {
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(index).toLowerCase() : '';
};

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.heic': 'image/heic',
};

export const resolveContactFileContentType = (file: Pick<File, 'name' | 'type'>): string => {
  const declared = file.type.toLowerCase().trim();
  if (declared && ALLOWED_MIME.has(declared)) return declared;
  return EXTENSION_CONTENT_TYPES[extensionOf(file.name)] ?? declared;
};

export const isContactFileTypeAllowed = (file: Pick<File, 'name' | 'type'>): boolean =>
  ALLOWED_MIME.has(file.type.toLowerCase()) || ALLOWED_EXTENSIONS.has(extensionOf(file.name));

export const getContactFileError = (file: Pick<File, 'name' | 'type' | 'size'>): string | null => {
  if (!isContactFileTypeAllowed(file)) {
    return `"${file.name}" isn't a supported type. Attach a PDF, Word, Excel, PowerPoint, CSV, TXT or image file.`;
  }

  if (file.size > CONTACT_FILE_MAX_BYTES) {
    return `"${file.name}" is ${formatFileSize(file.size)}; the limit is ${formatFileSize(
      CONTACT_FILE_MAX_BYTES,
    )}. Compress it or attach a smaller file.`;
  }

  return null;
};

export const ATTACHMENT_LIMIT_MESSAGE = `You can attach up to ${CONTACT_HISTORY_MAX_ATTACHMENTS} files to one entry. Remove a file to add another.`;

export const UPLOAD_FAILED_MESSAGE =
  "The file couldn't be uploaded. Check your connection and try again.";
